import { useQuery } from '@tanstack/react-query';
import { Autocomplete } from 'src/components/Autocomplete';
import { TextField } from 'src/components/TextField';
import api from 'src/utils/ajax';

interface Resource {
  id: number;
  caption: string;
}

interface ResourcesFilterProps {
  label: string;
  value: string | undefined;
  setValue: (newVal: string) => void;
  valueOnly?: boolean;
  multiple?: boolean;
}

export const ResourcesFilter: React.FC<ResourcesFilterProps> = ({
  label,
  value,
  setValue,
  valueOnly,
  multiple = true,
}) => {
  const { data: resources = [] } = useQuery({
    queryKey: ['resources'],

    queryFn: async (): Promise<Resource[]> => {
      const response = await api.get('/api/v3/resources', {
        params: {
          fetch_all: true,
          pluck_columns: 'id,caption',
          'q[s]': 'name asc',
        },
      });

      return response.data;
    },
  });

  const resourceIds = value ? String(value).split(',') : [];

  let selectedResource = undefined as Resource | undefined;
  let selectedResources = [] as Resource[];

  if (multiple) {
    selectedResources = resources.filter((r) =>
      resourceIds?.includes(String(r.id))
    );
  } else {
    selectedResource = resources.find((r) => String(r.id) === value);
  }

  if (valueOnly) {
    if (multiple) {
      return <>{selectedResources?.map((r) => r.caption)?.join(', ')}</>;
    }
    return <>{selectedResource?.caption || ''}</>;
  }

  return (
    <Autocomplete
      multiple={multiple}
      options={resources}
      getOptionLabel={(option: Resource) => option.caption}
      value={multiple ? selectedResources : selectedResource || null}
      onChange={(_, newValue) => {
        if (multiple) {
          setValue(
            (Array.isArray(newValue) ? newValue : []).map((r) => r.id).join(',')
          );
        } else {
          setValue(String(newValue?.id || ''));
        }
      }}
      renderInput={(params) => (
        <TextField {...params} label={label} placeholder="Select Resource" />
      )}
    />
  );
};
